import { createRouter, createWebHistory } from 'vue-router'

import { can, hasAnyAccess, isAuthenticated, loadSession } from '@/libs/auth'
import { decideNavigation }                                from '@/router/guard'
import { useAppStore }                                     from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path: '/',
            redirect: '/dashboard',
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Not the usual landing spot. The server's callback 302 sets the `__Host-tpm_session`
            // cookie and goes straight to the validated `?redirect=` destination the login button
            // carried — /dashboard, an instance page — so a normal sign-in never renders this
            // route at all; the browser lands directly on the target and `main.ts`'s /auth/me
            // bootstrap resolves there. No token, code or state ever reaches the SPA (D76).
            //
            // It stays because the sign-in is a chain of redirects a browser can be interrupted
            // in: a bookmarked or hand-typed /auth/callback, a stale tab replaying one, or a
            // deployment pointing the return path here must all still resolve into a signed-in
            // app rather than a spinner. `requiresAuth: false` because the guard must not bounce
            // it to /login on the one navigation where the session is being established —
            // Callback.vue awaits the bootstrap itself and decides (§9.1); it is a cosmetic
            // spinner and processes nothing.
            path:      '/auth/callback',
            name:      'auth-callback',
            component: () => import('@/views/auth/Callback.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Signed in, zero access (§10.2). It requires a session on purpose: an anonymous
            // visitor belongs at /login, not at a screen telling them to ask an administrator.
            path:      '/no-access',
            name:      'no-access',
            component: () => import('@/views/auth/NoAccess.vue'),
            meta:      { layout: 'Blank', requiresAuth: true },
        },
        {
            path:      '/dashboard',
            name:      'dashboard',
            component: () => import('@/views/Dashboard.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // D59's dedicated page, in v1 scope *in addition to* the dashboard segment (A2): §7.1
            // asks only for the segment, and at fleet scale the segment is not enough.
            //
            // The dashboard's tiles deep-link here with `?severity=`, `?type=` and `?instance_id=`
            // — the server's own filter fields (§6.1), so a bookmarked URL and the query that goes
            // on the wire are spelled the same way (`libs/issue.ts`).
            path:      '/issues',
            name:      'issues',
            component: () => import('@/views/issue/Issues.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/clients',
            name:      'clients',
            component: () => import('@/views/client/Clients.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/clients/:clientId',
            name:      'client',
            component: () => import('@/views/client/Client.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/instances',
            name:      'instances',
            component: () => import('@/views/instance/Instances.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // §9.1's instance shell. **Child routes rather than `?tab=`**, for four reasons the
            // plan states outright: each tab is its own lazy chunk (static-config and runtime pull
            // heavy ones, the overview must stay light); dashboard issues deep-link to a *specific*
            // tab; per-tab poll lifecycles bind to mount/unmount for free; and a tab is a place, so
            // it should have a URL. `UTabs` remains the visual, driven by `router.push`.
            //
            // Only `settings` has a screen in M2 (W2.15). The rest are `UEmpty` placeholders naming
            // the milestone that fills them — a route that exists and says "not yet" reads as an
            // unfinished panel, while a route that does not resolve reads as a broken one.
            path:      '/instances/:instanceId',
            component: () => import('@/views/instance/Instance.vue'),
            meta:      { requiresAuth: true },
            children:  [
                {
                    // The default child *is* overview (§9.1), so `/instances/:id` is a real
                    // destination rather than a redirect: every deep link the dashboard, the
                    // palette and the breadcrumb produce points at the bare instance URL.
                    path:      '',
                    name:      'instance-overview',
                    component: () => import('@/views/instance/tabs/Overview.vue'),
                    meta:      { requiresAuth: true, tab: 'overview' },
                },
                {
                    // Typed by hand or bookmarked from a future build; it must not 404 into an
                    // empty shell just because the canonical spelling is the bare path.
                    path:     'overview',
                    redirect: (to) => ({ name: 'instance-overview', params: to.params }),
                },
                {
                    path:      'runtime',
                    name:      'instance-runtime',
                    component: () => import('@/views/instance/tabs/Runtime.vue'),
                    meta:      { requiresAuth: true, tab: 'runtime' },
                },
                {
                    path:      'groups',
                    name:      'instance-groups',
                    component: () => import('@/views/instance/tabs/Groups.vue'),
                    meta:      { requiresAuth: true, tab: 'groups' },
                },
                {
                    path:      'snapshots',
                    name:      'instance-snapshots',
                    component: () => import('@/views/instance/tabs/Snapshots.vue'),
                    meta:      { requiresAuth: true, tab: 'snapshots' },
                },
                {
                    path:      'commands',
                    name:      'instance-commands',
                    component: () => import('@/views/instance/tabs/Commands.vue'),
                    meta:      { requiresAuth: true, tab: 'commands' },
                },
                {
                    path:      'certificates',
                    name:      'instance-certificates',
                    component: () => import('@/views/instance/tabs/Certificates.vue'),
                    meta:      { requiresAuth: true, tab: 'certificates' },
                },
                {
                    path:      'static-config',
                    name:      'instance-static-config',
                    component: () => import('@/views/instance/tabs/StaticConfig.vue'),
                    meta:      { requiresAuth: true, tab: 'static_config' },
                },
                {
                    path:      'access',
                    name:      'instance-access',
                    component: () => import('@/views/instance/tabs/Access.vue'),
                    meta:      { requiresAuth: true, tab: 'access' },
                },
                {
                    path:      'settings',
                    name:      'instance-settings',
                    component: () => import('@/views/instance/tabs/Settings.vue'),
                    meta:      { requiresAuth: true, tab: 'settings' },
                },
                {
                    path:      'install',
                    name:      'instance-install',
                    component: () => import('@/views/instance/tabs/Install.vue'),
                    meta:      { requiresAuth: true, tab: 'install' },
                },
            ],
        },
        {
            // §9.1's group editor — a **full page, not a dialog**, and deliberately *not* a child
            // of the instance shell: it is not one of the instance's ten tabs, and rendering it
            // inside the shell would put the tab bar of the screen the operator just left above the
            // editor they are working in.
            //
            // Its own four tabs (editor | yaml | references | history) are `?tab=` rather than
            // child routes — the opposite of the instance shell's choice, for the opposite reason.
            // The shell's tabs are separate chunks with separate poll lifecycles and no shared
            // state; these four share one draft, so remounting on a tab switch would throw away
            // what the operator is typing. `meta.tab` names the groups tab so the breadcrumb reads
            // *Client › Instance › Groups* (`layouts/components/Topbar.vue`).
            path:      '/instances/:instanceId/groups/new',
            name:      'instance-group-new',
            component: () => import('@/views/group/Group.vue'),
            meta:      { requiresAuth: true, tab: 'groups' },
        },
        {
            path:      '/instances/:instanceId/groups/:groupId',
            name:      'instance-group',
            component: () => import('@/views/group/Group.vue'),
            meta:      { requiresAuth: true, tab: 'groups' },
        },
        {
            // D74's in-stack registry page. **No `requiresManageUsers`-style flag on purpose**:
            // it is a read-only system page visible to every user who reaches the shell — a
            // zero-access account is already routed to /no-access by the guard, and the server
            // independently 403s both /registry endpoints for it (§9.6's cosmetic-gate rule, the
            // /dashboard reasoning).
            path:      '/registry',
            name:      'registry',
            component: () => import('@/views/registry/Registry.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // §9.1, §8: the users screens. `requiresManageUsers` is checked in the guard below —
            // cosmetic (§9.6, the sidebar entry is hidden the same way), the server independently
            // enforces `requireUserManager` on every route behind it.
            path:      '/users',
            name:      'users',
            component: () => import('@/views/user/Users.vue'),
            meta:      { requiresAuth: true, requiresManageUsers: true },
        },
        {
            path:      '/users/:userId',
            name:      'user',
            component: () => import('@/views/user/User.vue'),
            meta:      { requiresAuth: true, requiresManageUsers: true },
        },
        {
            // Self view — every signed-in user reaches it, `manage_users` or not (§9.1, §10.2's
            // "no role and no access" account included: a valid session may hold zero access and
            // still has an identity to look at).
            path:      '/profile',
            name:      'profile',
            component: () => import('@/views/Profile.vue'),
            meta:      { requiresAuth: true },
        },
    ],
})

/** vue-router's `meta` is an open `Record<…, unknown>`; the guard reads booleans and only those. */
const flag = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined)

router.beforeEach(async (to) => {
    const appStore = useAppStore()

    // The session lives in the server-set HttpOnly `__Host-tpm_session` cookie (D76), so "am I
    // signed in?" is the answer to `GET /auth/me`, not a synchronous cookie or token read. Every
    // navigation waits for that one bootstrap call to settle before deciding.
    //
    // `loadSession()` rather than `await appStore.sessionReady`: it is idempotent and returns the
    // in-flight promise, so this is the same single round trip `main.ts` starts — but the guard no
    // longer depends on having been reached *after* that line ran. A guard that awaits a
    // `sessionReady` which is still `null` would decide on an empty session instead of waiting.
    await loadSession()

    // The decision itself is pure and lives in `router/guard.ts`, where `yarn check:guard` can
    // walk a table of cases through it. Everything reactive is read here and passed in.
    return decideNavigation(
        {
            name:     typeof to.name === 'string' ? to.name : undefined,
            fullPath: to.fullPath,
            meta:     {
                requiresAuth:        flag(to.meta.requiresAuth),
                requiresManageUsers: flag(to.meta.requiresManageUsers),
            },
        },
        {
            settled:        appStore.isSessionSettled(),
            loggedIn:       isAuthenticated(),
            hasAnyAccess:   hasAnyAccess(),
            canManageUsers: can.manageUsers(),
        },
    )
})

export default router
